import React, { useEffect, useRef, useState } from "react";
import { usePlaylistsInfinite } from "../../hooks/usePlaylistsInfinite";
import { Playlist } from "../../schemas/playlistSchema";
import Loader from "../common/Loader";

interface PlaylistGridProps {
  selected: string[];
  onSelect: (id: string) => void;
}

const PlaylistGrid: React.FC<PlaylistGridProps> = ({ selected, onSelect }) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Debounce search so a query isn't fired per keystroke.
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [search]);

  const {
    playlists,
    total,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = usePlaylistsInfinite({
    type: "main",
    search: debouncedSearch || undefined,
  });

  // Load the next page when the grid is scrolled near the bottom.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const handleScroll = () => {
      if (isLoading || isFetchingNextPage || !hasNextPage) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
        fetchNextPage();
      }
    };

    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [isLoading, isFetchingNextPage, hasNextPage, fetchNextPage]);

  // A page that doesn't overflow the container can never be scrolled, so keep
  // pulling until the grid is tall enough for the scroll handler to take over.
  useEffect(() => {
    const el = listRef.current;
    if (!el || isLoading || isFetchingNextPage || !hasNextPage) return;
    if (el.scrollHeight <= el.clientHeight) {
      fetchNextPage();
    }
  }, [
    playlists.length,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  ]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-4 mb-4 shrink-0">
        <input
          type="text"
          placeholder="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 border border-gray-300 rounded px-3 py-2 m-4 focus:outline-none"
        />
        <span className="text-sm text-gray-600 whitespace-nowrap">
          {selected.length} selected
        </span>
      </div>
      {isLoading ? (
        <div className="text-center py-8 text-gray-400">
          <Loader />
        </div>
      ) : playlists.length === 0 ? (
        <div className="text-center py-8 text-gray-400">No playlists found</div>
      ) : (
        <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto pr-1">
          <div className="grid grid-cols-6 gap-4">
            {playlists.map((item: Playlist) => (
              <div
                key={item.id}
                className="relative bg-gray-100 rounded-lg shadow hover:shadow-lg transition p-2 flex flex-col items-center border border-gray-200"
                onClick={() => onSelect(item.id as string)}
              >
                {/* Thumbnail */}
                <div className="w-full h-20 flex items-center justify-center bg-black rounded">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 48 48"
                    className="w-12 h-12 text-white"
                  >
                    <rect width="48" height="48" rx="8" fill="#222" />
                    <polygon points="18,14 36,24 18,34" fill="#fff" />
                  </svg>
                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    checked={selected.includes(item.id as string)}
                    onChange={() => onSelect(item.id as string)}
                    onClick={(e) => e.stopPropagation()}
                    className="absolute top-2 right-2 w-4 h-4 accent-blue-600"
                  />
                </div>
                {/* Title */}
                <div className="mt-2 w-full text-center text-sm font-medium truncate">
                  {item.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlaylistGrid;
